import { useConsumptionData } from "@/api/consumption";
import { useConsumption } from "@/context/ConsumptionContext";
import useWindowWidth from "@/hooks/use-window-width";
import { formatCurrency, formatValue } from "@/lib/format-for-view";
import {
	add,
	endOfMonth,
	endOfYear,
	format,
	isAfter,
	isBefore,
	startOfMonth,
	startOfYear,
	sub,
} from "date-fns";
import React, { useMemo } from "react";
import {
	Bar,
	BarChart,
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

interface UsageData {
	time?: string;
	usage?: number;
	cost?: number;
	temperature?: number;
	subscription?: number;
}

interface YearlyUsageData {
	time?: string;
	creuses_previous?: number;
	creuses_current?: number;
	pleines_previous?: number;
	pleines_current?: number;
	abonnement_previous?: number;
	abonnement_current?: number;
}

type EnergyUsageData = (UsageData | YearlyUsageData) & {
	timestamp?: string;
};

const CustomTooltip = React.memo(
	({
		active,
		payload,
		label,
		viewMode,
		timeframe,
	}: {
		active?: boolean;
		payload?: {
			dataKey: string;
			value: number;
			color: string;
			payload: Record<string, number>;
		}[];
		label?: string;
		viewMode: "kWh" | "Euro";
		timeframe: "Daily" | "Weekly" | "Monthly" | "Yearly";
	}) => {
		const content = useMemo(() => {
			if (!active || !payload || !payload.length) return null;
			const sharedData = payload[0].payload;
			const isCurrentYear = payload[0].dataKey.includes("current");
			const year = isCurrentYear ? "2025" : "2024";

			return (
				<div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200 text-xs z-50">
					<p className="font-semibold mb-1">{label}</p>

					{timeframe === "Yearly" ? (
						<>
							<p className="text-xs font-medium text-gray-500 mb-1">{year}</p>
							<p className="mb-1 flex items-center text-teal-600">
								<span
									className="inline-block w-2 h-2 rounded-full mr-2"
									style={{
										backgroundColor: isCurrentYear ? "#93E6D1" : "#FB8266",
									}}
								/>
								{`HC: ${(isCurrentYear ? sharedData.creuses_current : sharedData.creuses_previous)?.toFixed(1) || 0} kWh`}
							</p>
							<p className="mb-1 flex items-center text-teal-600">
								<span
									className="inline-block w-2 h-2 rounded-full mr-2"
									style={{
										backgroundColor: isCurrentYear ? "#2A9D90" : "#FC9F80",
									}}
								/>
								{`HP: ${(isCurrentYear ? sharedData.pleines_current : sharedData.pleines_previous)?.toFixed(1) || 0} kWh`}
							</p>
							{viewMode === "Euro" && (
								<p className="mb-1 flex items-center text-gray-900">
									<span
										className="inline-block w-2 h-2 rounded-full mr-2"
										style={{ backgroundColor: "#0F172A" }}
									/>
									{`Abonnement: ${formatCurrency(isCurrentYear ? sharedData.abonnement_current : sharedData.abonnement_previous)} `}
								</p>
							)}
						</>
					) : (
						<>
							{/* Usage */}
							{sharedData.usage !== 0 && (
								<p className="mb-1 flex items-center text-teal-600">
									<span
										className="inline-block w-2 h-2 rounded-full mr-2"
										style={{ backgroundColor: "#2A9D90" }}
									/>
									{viewMode === "Euro"
										? formatCurrency(sharedData.cost)
										: formatValue(sharedData.usage, viewMode)}
								</p>
							)}

							{/* Temperature */}
							{
								<p className="mb-1 flex items-center text-pink-500">
									<span
										className="inline-block w-2 h-2 rounded-full mr-2"
										style={{ backgroundColor: "#ec4899" }}
									/>
									{`${(sharedData.temperature || 0)?.toFixed(1)}°C`}
								</p>
							}
							{viewMode === "Euro" && (
								<p className="mb-1 flex items-center text-gray-900">
									<span
										className="inline-block w-2 h-2 rounded-full mr-2"
										style={{ backgroundColor: "#0F172A" }}
									/>
									{`${formatCurrency(sharedData.subscription || 0)} `}
								</p>
							)}
						</>
					)}
				</div>
			);
		}, [active, payload, label, viewMode, timeframe]);

		return content;
	},
);

const granularityMap = {
	Daily: "HOURLY",
	Weekly: "DAILY",
	Monthly: "DAILY",
	Yearly: "MONTHLY",
} as const;

const timestampMap = {
	Daily: "HH:mm",
	Weekly: "dd/MM",
	Monthly: "dd/MM",
	Yearly: "MM",
};

export default function EnergyUsageChart() {
	const { unit, timeframe, selectedDate } = useConsumption();

	const windowWidth = useWindowWidth();
	const RIGHT_AXIS_WIDTH = 35;

	let startDate = selectedDate || sub(new Date(), { days: 2 });
	let endDate = add(startDate, { days: 1 });
	const yesterday = sub(new Date(), { days: 1 });

	const granularity = granularityMap[timeframe];

	if (timeframe === "Daily") {
		endDate = startDate;
	}

	if (timeframe === "Weekly") {
		const baseDate = selectedDate || sub(new Date(), { days: 1 });
		endDate = baseDate;
		startDate = sub(endDate, { days: 6 });
	}

	if (timeframe === "Monthly") {
		startDate = startOfMonth(startDate);
		endDate = endOfMonth(startDate);

		// Prevent going beyond yesterday
		if (isAfter(endDate, yesterday)) {
			endDate = yesterday;
		}
	}

	if (timeframe === "Yearly") {
		startDate = startOfYear(startDate);
		endDate = add(startDate, { years: 1 });
		if (isAfter(endDate, yesterday)) {
			endDate = sub(yesterday, { days: 1 });
		}
	}

	const {
		data: consumptionData,
		isLoading,
		error,
	} = useConsumptionData({
		energy: "ELEC",
		granularity: granularity,
		start_date: format(startDate, "yyyy-MM-dd"),
		end_date: format(endDate, "yyyy-MM-dd"),
	});

	const { data: lastYearData, isLoading: isLoadingLastYearData } =
		useConsumptionData({
			energy: "ELEC",
			granularity: granularity,
			start_date: format(sub(startDate, { years: 1 }), "yyyy-MM-dd"),
			end_date: format(endOfYear(sub(endDate, { years: 1 })), "yyyy-MM-dd"),
			disabled: timeframe !== "Yearly",
		});

	const data = useMemo(() => {
		const result: EnergyUsageData[] = [];

		if (
			timeframe === "Yearly" &&
			consumptionData?.data &&
			Array.isArray(consumptionData.data) &&
			lastYearData?.data &&
			Array.isArray(lastYearData.data)
		) {
			const months = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
			for (const month of months) {
				const currentYearData = consumptionData.data.find(
					(item) => new Date(item.timestamp).getMonth() === month,
				);
				const previousYearData = lastYearData.data.find(
					(item) => new Date(item.timestamp).getMonth() === month,
				);
				const currentDate = new Date();
				currentDate.setMonth(month);
				const time = format(currentDate, timestampMap[timeframe]);
				result.push({
					timestamp: currentYearData?.timestamp,
					time: time,
					abonnement_current: currentYearData?.subscription_cost,
					abonnement_previous: previousYearData?.subscription_cost,
					creuses_current: currentYearData?.consumption_hc,
					creuses_previous: previousYearData?.consumption_hc,
					pleines_current: currentYearData?.consumption_hp,
					pleines_previous: previousYearData?.consumption_hp,
				});
			}
		} else if (consumptionData && Array.isArray(consumptionData.data)) {
			for (const item of consumptionData.data) {
				result.push({
					timestamp: item.timestamp,
					time: format(new Date(item.timestamp), timestampMap[timeframe]),
					usage: item.consumption,
					cost: item.cost,
					subscription: item.subscription_cost,
					temperature: item.temperature,
				});
			}
		}

		if (timeframe === "Daily") {
			return result.filter((item) => {
				const date = new Date(item.timestamp || "");

				return isAfter(date, startDate) || isBefore(date, endDate);
			});
		}

		return result;
	}, [consumptionData, lastYearData, timeframe, endDate, startDate]);

	const getBarSize = () => {
		if (windowWidth < 480) return 8;
		if (windowWidth < 768) return 12;
		return 16;
	};

	const formatKwhTick = (value: number) => {
		const n = Number(value);
		if (!Number.isFinite(n)) return "0kWh";
		return `${n.toFixed(0)}kWh`;
	};

	// Note: We rely on allowDecimals={false} and domain={[0, 'dataMax']} to avoid repeated 0.0 labels

	if (consumptionData?.data.length === 0) {
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="text-red-500">
						Aucune donnée trouvée pour cette période
					</div>
				</CardContent>
			</Card>
		);
	}

	if (error) {
		console.error("Error fetching consumption data:", error);
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="text-red-500">
						Erreur lors du chargement des données de consommation. Veuillez
						réessayer ultérieurement.
					</div>
				</CardContent>
			</Card>
		);
	}

	if (isLoading || (timeframe === "Yearly" && isLoadingLastYearData)) {
		return (
			<Card className="w-full max-w-md">
				<CardContent className="px-3 sm:px-6 py-6">
					<div className="animate-pulse space-y-4">
						<div className="h-4 bg-gray-200 rounded w-1/4" />
						<div className="h-6 bg-gray-200 rounded w-3/4" />
						<div className="h-8 bg-gray-200 rounded w-1/2" />
						<div className="h-4 bg-gray-200 rounded w-2/3" />
					</div>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card className="w-full max-w-full">
			<CardHeader className="pb-2">
				<CardTitle className="text-lg font-semibold">
					Consommation d'énergie
				</CardTitle>
				<p className="text-xs text-gray-400">
					{timeframe === "Daily"
						? format(startDate, "dd/MM/yyyy")
						: timeframe === "Weekly"
							? `${format(startDate, "dd/MM/yyyy")} - ${format(endDate, "dd/MM/yyyy")}`
							: timeframe === "Monthly"
								? `${format(startDate, "dd/MM/yyyy")} - ${format(endDate, "dd/MM/yyyy")}`
								: `${format(startDate, "dd/MM/yyyy")} - ${format(endDate, "dd/MM/yyyy")}`}
				</p>
			</CardHeader>
			<CardContent className="p-2 pt-0">
				<div className="h-[280px] sm:h-[300px] md:h-[360px] relative">
					<ResponsiveContainer className="z-0" width="100%" height="100%">
						{timeframe === "Yearly" && unit === "kWh" ? (
							<BarChart
								margin={{ right: 10 }}
								data={data}
								barGap={2}
								barCategoryGap={25}
							>
								<XAxis
									dataKey="time"
									tick={{ fontSize: 10 }}
									axisLine={false}
									tickLine={false}
								/>
								<YAxis
									axisLine={false}
									tickLine={false}
									width={45}
									tickFormatter={formatKwhTick}
									domain={[0, "dataMax"]}
									tickCount={5}
									fill="#666"
									fontSize={10}
								/>
								<Tooltip
									content={
										<CustomTooltip viewMode={unit} timeframe={timeframe} />
									}
									wrapperStyle={{ zIndex: 1000 }}
									isAnimationActive={false}
								/>
								<Bar
									dataKey="creuses_previous"
									stackId="previous"
									fill="#FB8266"
									barSize={getBarSize()}
								/>
								<Bar
									dataKey="pleines_previous"
									stackId="previous"
									fill="#FC9F80"
									barSize={getBarSize()}
								/>
								<Bar
									dataKey="pleines_current"
									stackId="current"
									fill="#2A9D90"
									barSize={getBarSize()}
								/>
								<Bar
									dataKey="creuses_current"
									stackId="current"
									fill="#93E6D1"
									barSize={getBarSize()}
								/>
							</BarChart>
						) : timeframe === "Yearly" && unit === "Euro" ? (
							<BarChart
								margin={{ right: 10 }}
								data={data}
								barGap={2}
								barCategoryGap={25}
							>
								<XAxis
									dataKey="time"
									tick={{ fontSize: 10 }}
									axisLine={false}
									tickLine={false}
								/>
								<YAxis
									axisLine={false}
									tickLine={false}
									width={45}
									tickFormatter={(value) => formatValue(Number(value), "Euro")}
									domain={[0, "dataMax"]}
									tickCount={5}
									fill="#666"
									fontSize={10}
								/>
								<Tooltip
									content={
										<CustomTooltip viewMode={unit} timeframe={timeframe} />
									}
									isAnimationActive={false}
								/>
								<Bar
									dataKey="abonnement_previous"
									stackId="previous"
									fill="#0F172A"
									barSize={getBarSize()}
								/>
								<Bar
									dataKey="creuses_previous"
									stackId="previous"
									fill="#FB8266"
									barSize={getBarSize()}
								/>
								<Bar
									dataKey="pleines_previous"
									stackId="previous"
									fill="#FC9F80"
									barSize={getBarSize()}
								/>
								<Bar
									dataKey="abonnement_current"
									stackId="current"
									fill="#0F172A"
									barSize={getBarSize()}
								/>
								<Bar
									dataKey="creuses_current"
									stackId="current"
									fill="#93E6D1"
									barSize={getBarSize()}
								/>
								<Bar
									dataKey="pleines_current"
									stackId="current"
									fill="#2A9D90"
									barSize={getBarSize()}
								/>
							</BarChart>
						) : (
							<BarChart
								margin={{ right: RIGHT_AXIS_WIDTH }}
								data={data}
								barGap={5}
							>
								<XAxis
									dataKey="time"
									axisLine={false}
									tickLine={false}
									tick={{ fontSize: 10 }}
								/>
								<YAxis
									axisLine={false}
									tickLine={false}
									width={45}
									tickFormatter={(value) =>
										unit === "kWh"
											? formatKwhTick(Number(value))
											: formatValue(Number(value), unit)
									}
									domain={[0, "dataMax"]}
									tickCount={5}
									fill="#666"
									fontSize={10}
								/>
								<Tooltip
									content={
										<CustomTooltip viewMode={unit} timeframe={timeframe} />
									}
									wrapperStyle={{ zIndex: 1000 }}
									cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
									isAnimationActive={false}
								/>
								{unit === "Euro" && (
									<Bar
										dataKey="subscription"
										stackId="a"
										fill="#0F172A"
										barSize={getBarSize()}
									/>
								)}
								<Bar
									dataKey={unit === "kWh" ? "usage" : "cost"}
									stackId={unit === "Euro" ? "a" : undefined}
									fill="#2A9D90"
									radius={unit === "Euro" ? undefined : [5, 5, 0, 0]}
									barSize={getBarSize()}
								/>
							</BarChart>
						)}
					</ResponsiveContainer>

					{timeframe !== "Yearly" && (
						<div className="absolute z-10 inset-0 pointer-events-none">
							<ResponsiveContainer width="100%" height="100%">
								<LineChart
									data={data}
									margin={{
										top: 10,
										right: 0,
										left: 45,
										bottom: 0,
									}}
									className="text-[#EC4899]"
								>
									<YAxis
										orientation="right"
										axisLine={false}
										tickLine={false}
										tick={{ fontSize: 10 }}
										width={RIGHT_AXIS_WIDTH}
										tickFormatter={(value) => `${value}°C`}
									/>
									<Line
										type="monotone"
										dataKey="temperature"
										stroke="currentColor"
										strokeWidth={2}
										dot={{ r: 3, fill: "currentColor", strokeWidth: 1 }}
										isAnimationActive={false}
									/>
								</LineChart>
							</ResponsiveContainer>
						</div>
					)}
				</div>

				<div className="flex flex-col items-center">
					{timeframe === "Yearly" && (
						<div className="flex justify-center gap-16 text-sm font-medium text-gray-600 mt-3">
							<div>2025</div>
							<div>2024</div>
						</div>
					)}

					<ChartLegends timeframe={timeframe} viewMode={unit} />
				</div>
			</CardContent>
		</Card>
	);
}

function ChartLegends({
	timeframe,
	viewMode,
}: {
	timeframe: string;
	viewMode: string;
}) {
	if (timeframe === "Yearly") {
		return (
			<div className="flex flex-col items-center gap-2 text-xs text-gray-600">
				<div className="grid grid-cols-2 gap-x-8 justify-center">
					<div className="flex items-center gap-1">
						<span className="w-3 h-3 rounded-sm bg-[#93E6D1]" />
						<span className="text-xs">Heures Creuses</span>
					</div>
					<div className="flex items-center gap-1">
						<span className="w-3 h-3 rounded-sm bg-[#EF9C88]" />
						<span className="text-xs">Heures Creuses</span>
					</div>
					<div className="flex items-center gap-1">
						<span className="w-3 h-3 rounded-sm bg-[#2A9D90]" />
						<span className="text-xs">Heures Pleines</span>
					</div>
					<div className="flex items-center gap-1">
						<span className="w-3 h-3 rounded-sm bg-[#FB8266]" />
						<span className="text-xs">Heures Pleines</span>
					</div>
				</div>

				{viewMode === "Euro" && (
					<div className="flex items-center gap-1 mt-1">
						<span className="w-3 h-3 rounded-sm bg-[#0F172A]" />
						<span className="text-xs">Abonnement</span>
					</div>
				)}
			</div>
		);
	}

	return (
		<div className="flex flex-wrap justify-center gap-4 mt-4 px-4">
			{viewMode === "Euro" && (
				<div className="flex items-center gap-2">
					<span className="w-3 h-3 rounded-sm bg-[#0F172A]" />
					<span className="text-xs text-gray-600">Abonnement</span>
				</div>
			)}
			<div className="flex items-center gap-2">
				<span className="w-3 h-3 rounded-sm bg-[#2A9D90]" />
				<span className="text-xs text-gray-600">Consommation</span>
			</div>
			<div className="flex items-center gap-2">
				<span className="w-3 h-3 rounded-sm bg-[#EC4899]" />
				<span className="text-xs text-gray-600">Température</span>
			</div>
		</div>
	);
}
